import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { systemService, profileService } from '../services';
import type { GeneralSettings, SecuritySettings, AccountSettings } from '../services';

interface SettingsState {
    general: GeneralSettings | null;
    security: SecuritySettings | null;
    userPrefs: AccountSettings | null;
    featureFlags: Record<string, boolean>;
    tablePageSize: number;
    loading: boolean;
    error: string | null;

    fetchGeneral: () => Promise<void>;
    updateGeneral: (settings: GeneralSettings) => Promise<void>;
    fetchSecurity: () => Promise<void>;
    setSecurity: (settings: SecuritySettings) => void;
    fetchUserPrefs: () => Promise<void>;
    fetchFeatureFlags: () => Promise<void>;
    isFeatureEnabled: (key: string) => boolean;
    getDateFormatSettings: () => { timeZone: string; dateFormat: string; timeFormat: '12h' | '24h' };
    setTablePageSize: (size: number) => void;
}

const defaultGeneral: GeneralSettings = {
    systemName: 'VSM Orchestrator',
    companyName: 'SecureLink',
    platformUrl: '',
    appDownloadIos: '',
    appDownloadAndroid: '',
    appDownloadMacos: '',
    appDownloadWindows: '',
    timeZone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '24h',
    language: 'English',
    apiBaseUrl: '/vsm',
    apiTimeout: 15,
    rateLimiting: { enabled: true, maxRequestsPerMinute: 60 },
    sessionManagement: { timeoutMinutes: 480, idleTimeoutMinutes: 15, rememberMeDays: 30, forceLogoutOnPasswordChange: true },
};

export const useSettingsStore = create<SettingsState>()(
    persist(
        (set, get) => ({
            general: null,
            security: null,
            userPrefs: null,
            featureFlags: {},
            tablePageSize: 10,
            loading: false,
            error: null,

            fetchGeneral: async () => {
                try {
                    set({ loading: true, error: null });
                    const settings = await systemService.getGeneral();
                    set({ general: settings, loading: false });
                } catch (err: any) {
                    set({ error: err.message, loading: false });
                    // If fetch fails, we might want to keep the persisted state or use defaults
                    if (!get().general) {
                        set({ general: defaultGeneral });
                    }
                }
            },

            updateGeneral: async (settings: GeneralSettings) => {
                try {
                    set({ loading: true, error: null });
                    const updated = await systemService.updateGeneral(settings);
                    set({ general: updated, loading: false });
                } catch (err: any) {
                    set({ error: err.message, loading: false });
                    throw err;
                }
            },

            // VSNP-704 follow-up: the idle/session timeouts live in the
            // `security` category, NOT `general`. The original hook read
            // from general.sessionManagement which is a separate DB row
            // never touched by the Security Settings tab. Holding the
            // security blob here so useIdleTimeout sees the operator's
            // edit immediately, and so it survives a tab reload.
            fetchSecurity: async () => {
                try {
                    const settings = await systemService.getSecurity();
                    set({ security: settings });
                } catch (err: any) {
                    console.error('Failed to fetch security settings:', err.message);
                }
            },

            setSecurity: (settings: SecuritySettings) => set({ security: settings }),

            fetchUserPrefs: async () => {
                try {
                    const prefs = await profileService.getSettings();
                    set({ userPrefs: prefs });
                } catch (err: any) {
                    console.error('Failed to fetch user preferences:', err.message);
                }
            },

            fetchFeatureFlags: async () => {
                try {
                    const flags = await systemService.getFeatureFlags();
                    set({ featureFlags: flags });
                } catch (err: any) {
                    console.error('Failed to fetch feature flags:', err.message);
                    // Default to all enabled if fetch fails
                }
            },

            isFeatureEnabled: (key: string) => {
                const flags = get().featureFlags;
                // Default to true if not present
                if (flags[key] === undefined) return true;
                return flags[key];
            },

            setTablePageSize: (size: number) => set({ tablePageSize: size }),

            getDateFormatSettings: () => {
                const general = get().general || defaultGeneral;
                const prefs = get().userPrefs;
                return {
                    timeZone: prefs?.timeZone || general.timeZone || 'UTC',
                    dateFormat: prefs?.dateFormat || general.dateFormat || 'MM/DD/YYYY',
                    timeFormat: (prefs?.timeFormat || general.timeFormat || '24h') as '12h' | '24h',
                };
            },
        }),
        {
            name: 'system-settings-storage',
            storage: createJSONStorage(() => localStorage),
        }
    )
);
